import { FC, Fragment, useEffect, useState } from 'react';
import { BookingModel, FlightInfo, Pax } from 'ts/dynamic/interfaces/DynamicBookResponse';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import { useTranslation } from 'react-i18next';
import BookPayment from 'components/shared/BookPayment';
import { Credit2000PaymentInfo, PrivilegesInfo } from 'ts/shared/interfaces/Generic';
import GeneralConditionsModal from 'components/shared/GeneralConditionsModal';
import OrderTripCompany from 'components/shared/OrderTripCompany';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { getDynPassengerValidation } from 'utils/dynamic/dynamic';
import { showError } from 'utils/utils';
import { validate } from 'utils/validation';
import { book } from 'services/dynamic';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import useRecaptcha from 'hooks/useRecaptcha';
import DynamicFlightForm from './DynamicFlightForm';
import DynamicTicketForm from './DynamicTicketForm';
import { b2b } from 'constants/generic';

interface DynamicBookFormProps {
  model: BookingModel;
  flight?: FlightInfo;
  privilegesInfo: PrivilegesInfo;
  onClickBook: () => void;
  couponAmount: string;
  setModel: (model: BookingModel) => void;
  addPrice: number;
  onUpdateAddPrice: (addPrice: number) => void;
  paxes: Pax[];
  setPaxes: (paxes: Pax[]) => void;
}

const DynamicBookForm: FC<DynamicBookFormProps> = ({ paxes, setPaxes, ...props }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [urlParams] = useSearchParams();
  const { handleRecapcha } = useRecaptcha();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [companyId, setCompanyId] = useState('');
  const [tripId, setTripId] = useState('0');
  const [isConditionsOpen, setIsConditionsOpen] = useState(false);
  const isTicketOnly = urlParams.get('matrixConfig')?.toLowerCase() === 'dynamic_am';

  useEffect(() => {
    if (isTicketOnly) {
      return;
    }
    if (b2b && pathname !== '/dynamic/book') {
      return;
    }

    (async () => {
      const newPaxes = props.model.paxes.map((pax) => ({
        ...pax,
        gender: pax.gender || 'M',
      }));
      setPaxes(newPaxes);
    })();
  }, []); // eslint-disable-line

  const isValid = () => {
    let result = true;
    paxes.forEach((pax, index) => {
      const validation = getDynPassengerValidation(index);
      if (!validate(validation, pax)) {
        result = false;
      }
    });

    return result && termsAccepted;
  };

  const onClickSend = async () => {
    setIsSubmitted(true);

    const token = await handleRecapcha();
    if (!token) return;
    if (isValid()) {
      setIsLoading(true);
      await props.onClickBook();

      const response = await book(
        token,
        urlParams.get('searchToken')!,
        paxes,
        false,
        props.model.credit2000PaymentInfo,
        tripId,
        companyId,
        parseInt(props.couponAmount || '0')
      );

      if (response) {
        if (response.sessionExpired) {
          showError(t('home.session_expired'));
        } else if (response.hasErrors) {
          console.log('Dynamic/Book ' + response.errorMessage);
          showError(t('something_went_wrong'));
        } else {
          sessionStorage.removeItem('screenshotsDynamic');
          sessionStorage.setItem('dynSuccessOrder', JSON.stringify(response.bookingModel));
          navigate('/dynamic/success');
        }
      }
      setIsLoading(false);
    }
  };

  const onClickSubmit = async (credit2000PaymentInfo: Credit2000PaymentInfo) => {
    setIsSubmitted(true);

    const token = await handleRecapcha();
    if (!token) return;
    if (isValid()) {
      setIsLoading(true);
      await props.onClickBook();

      const response = await book(
        token,
        urlParams.get('searchToken')!,
        paxes,
        true,
        credit2000PaymentInfo,
        tripId,
        companyId,
        parseInt(props.couponAmount || '0')
      );

      if (response) {
        if (response.sessionExpired) {
          showError(t('home.session_expired'));
        } else if (response.hasErrors) {
          console.log('Dynamic/Book ' + response.errorMessage);
          showError(t('something_went_wrong'));
        } else {
          sessionStorage.removeItem('screenshotsDynamic');
          sessionStorage.setItem('dynPaymentOrder', JSON.stringify(response));
          navigate('/dynamic/payment');
        }
      }
      setIsLoading(false);
    }
  };

  return (
    <div className="details book">
      <div className="book-form default-shadow">
        <div className="title-underline text-lg mb-20 title-header-grey">
          {t('passenger.details')}
        </div>

        {!!props.flight && (
          <DynamicFlightForm
            flight={props.flight}
            paxes={paxes}
            setPaxes={setPaxes}
            isSubmitted={isSubmitted}
            addPrice={props.addPrice}
            onUpdateAddPrice={props.onUpdateAddPrice}
            currency={props.model.currency || ''}
          />
        )}
        {!!props.model.eventInfo && !props.flight && (
          <DynamicTicketForm
            paxes={paxes}
            setPaxes={setPaxes}
            isSubmitted={isSubmitted}
            maxPaxes={props.model.eventInfo.attraction.tickets[0]?.ticket.qnt}
          />
        )}

          <OrderTripCompany
            companyId={companyId}
            tripId={tripId}
            onChangeCompanyId={setCompanyId}
            onChangeTripId={setTripId}
          />
        
        <div className="checkbox-inline">
          <Checkbox
            checked={termsAccepted}
            onChange={setTermsAccepted}
            className="mb-30"
            hasError={isSubmitted && !termsAccepted}
            blicked={true}
            htmlLabel={
              <Fragment>
                <span className="mr-10">{t('passenger.confirm')}</span>
                <span
                  className="btn-lnk"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsConditionsOpen(true);
                  }}
                >
                  {t('passenger.read_more')}
                </span>
              </Fragment>
            }
          />
        </div>
        {isConditionsOpen && (
          <GeneralConditionsModal
            product="Dynamic_FMHM"
            onClose={() => setIsConditionsOpen(false)}
          />
        )}

          <BookPayment
            onClickSend={onClickSend}
            onClickSubmit={onClickSubmit}
            disabled={isLoading}
            privileges={props.privilegesInfo}
            credit2000={props.model.credit2000PaymentInfo}
            isFormValid={isValid()}
            systemProducts="Dynamic"
          />
        
      </div>
    </div>
  );
};

export default withErrorBoundary(DynamicBookForm, 'DynamicBookForm');
